import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import DashboardIcon from '@assets/images/icons/dashboard.svg'
import MasterDataIcon from '@assets/images/icons/masterData.svg'
import DisposalIcon from '@assets/images/icons/disposal.svg'
import ColoredFinanceIcon from '@assets/images/icons/finances_colored.svg'
import FinanceIcon from '@assets/images/icons/finances.svg'
import SettingsIcon from '@assets/images/icons/settings_menu.svg'
import DocumentIcon from '@assets/images/icons/menu/menu-document.svg'
import OfficeIcon from '@assets/images/icons/menu/menu-office.svg'
import StaffIcon from '@assets/images/icons/staff.svg'
import ArrowUp from '@assets/images/icons/arr_up.svg'
import MarketPlaceIcon from '@assets/images/icons/marketplace.svg'
import BookingIcon from '@assets/images/icons/menu/menu-booking.svg'
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded'
import AppsIcon from '@assets/images/icons/apps.svg'
import FirstStepsIcon from '@assets/images/icons/rocketMenu.svg'
import CloudIcon from '@assets/images/icons/cloud.svg'
import BellIcon from '@assets/images/icons/bell.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { user } from 'ampersand-app' // FLAG:useDocuAi — read the per-tenant experimental flag
import { experimentalFeatures, MenuItems, subscriptionLimits, SubscriptionTypeTypes } from '@/modules/base/util/constants'
import { MenuTypes } from '@/modules/layout/constants'
import ThemeInterface from '@/theme-interface'
import { SubmenuItemInterface } from '@/modules/layout/components/menuV2/menu-popover/menu-popover-item'
import openStartTrialModal from '@/modules/base/components/modal/_helpers/openStartTrialModal'
import { isMeinBuero } from '@/utils/build-env'
import { useAppSelector } from '@/redux/store'
import useMenuPopover from '@/modules/layout/components/menuV2/useMenuPopover'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'

const isMB = isMeinBuero()

export const MenuItemIcons = {
  procurement: DisposalIcon,
  dashboard: DashboardIcon,
  office: OfficeIcon,
  todos: OfficeIcon,
  masterData: MasterDataIcon,
  clients: MasterDataIcon,
  disposal: DisposalIcon,
  finances: isMB ? ColoredFinanceIcon : FinanceIcon,
  mainBooking: BookingIcon,
  settings: SettingsIcon,
  documents: DocumentIcon,
  staff: StaffIcon,
  apps: AppsIcon,
  marketplace: MarketPlaceIcon,
  obtaining: ({ iconSize }) => (
    <ShoppingCartRoundedIcon sx={{ fontSize: iconSize?.width || 14 }} />
  ),
  firstSteps: FirstStepsIcon,
  backup: CloudIcon,
  // FLAG:docuAiEnhancements — icon for the flag-gated "Meine Meldungen" entry (menu/config.js).
  // Flag-off no menu item is named `customerIssues`, so this mapping is inert for the baseline.
  customerIssues: BellIcon,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  rootOmo: {
    display: 'flex',
    alignItems: 'center',
    color: theme.white,
    cursor: 'pointer',
    padding: 10,
    margin: '0px -10px',
    // the global `a:focus` reset would colour a focused item like an active one - e.g. "Shop" once the
    // checkout is closed and hands the focus back to it
    '&:focus': {
      color: theme.white,
    },
    '&:hover': {
      color: theme.primaryBright,
    },
    '&:hover $icon': {
      fill: theme.primaryBright,
    },
    '&:hover $arrow': {
      fill: theme.primaryBright,
    },
    '& $arrow': {
      transform: 'rotate(180deg)',
    },
    '&:hover $icon path': {
      fill: theme.primaryBright,
    },
  },
  rootMbo: {
    display: 'flex',
    alignItems: 'center',
    color: theme.white,
    cursor: 'pointer',
    padding: 10,
    margin: '0px -10px',
    '&:focus': {
      color: theme.white,
    },
    '&:hover': {
      color: theme.primaryBright,
    },
    '&:hover $arrow': {
      fill: theme.primaryBright,
    },
    '& $arrow': {
      transform: 'rotate(180deg)',
    },
  },
  icon: {
    fill: theme.white,
  },
  title: {
    marginLeft: 10,
    lineHeight: 'normal',
  },
  // the states below keep their colour on a focused item as well (see rootOmo)
  activeOmo: {
    color: theme.primaryBright,
    fontWeight: theme.fontWeights.medium,
    '&:focus': {
      color: theme.primaryBright,
    },
    '& $icon': {
      fill: theme.primaryBright,
    },
    '& $icon path': {
      fill: theme.primaryBright,
    },
    '& $arrow': {
      fill: theme.primaryBright,
    },
  },
  activeMbo: {
    color: theme.primaryBright,
    fontWeight: theme.fontWeights.medium,
    '&:focus': {
      color: theme.primaryBright,
    },
    '& $arrow': {
      fill: theme.primaryBright,
    },
  },
  expired: {
    color: theme.disabledColor,
    '&:focus': {
      color: theme.disabledColor,
    },
    '& $icon': {
      fill: theme.disabledColor,
    },
    '& $arrow': {
      fill: theme.disabledColor,
    },
  },
  openOmo: {
    color: theme.primaryBright,
    '&:focus': {
      color: theme.primaryBright,
    },
    '& $arrow': {
      transform: 'rotate(0deg)',
      fill: theme.primaryBright,
    },
    '& $icon': {
      fill: theme.primaryBright,
    },
  },
  openMbo: {
    color: theme.primaryBright,
    '&:focus': {
      color: theme.primaryBright,
    },
    '& $arrow': {
      transform: 'rotate(0deg)',
      fill: theme.primaryBright,
    },
  },
  arrow: {
    marginLeft: 6,
    fill: theme.white,
  },
  badge: {
    borderRadius: '50%',
    color: theme.white,
    backgroundColor: theme.error,
    fontSize: theme.fontSizes.extrasmall,
    textAlign: 'center',
    position: 'absolute',
    right: 2,
    top: 3,
    transform: 'translate(100%, -50%)',
    height: 15,
    width: 15,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
  },
  badgeLabelIcon: {
    position: 'relative',
  },
  iconWrapper: {
    display: 'flex',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}), { name: 'MenuBarItem' })

interface MenuBarItemProps {
  name: string,
  title: string,
  url: string,
  showOnlyIcon: boolean,
  isIncludedInPlan: boolean,
  requiredSub: {
    exists: boolean,
    isExpired: boolean,
    type: keyof typeof SubscriptionTypeTypes
  }
  iconSize: { height: number, width: number },
  submenuItems: SubmenuItemInterface[],
  badgeLabel: string | number,
  iconClassName?: string,
}
const defaultIconSize = { height: 14, width: 14 }

const MenuBarItem = (props: MenuBarItemProps) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const openUpgradeModal = useUpgradeModal()
  const menuSettings = useAppSelector(state => state.user.info.layoutSettings?.menu)
  const { subscriptions } = useAppSelector(state => state.subscription?.details)

  const menuItemRef = useRef()
  const {
    name,
    title,
    submenuItems,
    showOnlyIcon,
    iconSize = defaultIconSize,
    isIncludedInPlan,
    requiredSub,
    url,
    badgeLabel = '',
    iconClassName,
  } = props
  const notTrialSub = useMemo(() => subscriptions.find((sub) => !sub.isTrial), [subscriptions])
  const menuIconSize = useMemo(() => (iconSize), [])
  const itemId = `menu-bar-item-${name}`
  const {
    target,
    isOpen,
    setOpen,
    setTarget,
    setMenuItems,
  } = useMenuPopover()

  const isActive = useMemo(() => {
    if (name === MenuItems.Dashboard && location.pathname === '/') { return true }
    const match = location.pathname.match(/^\/([^/]+)/)
    const urls = [match?.[0], location.pathname]
    const submenuItem = submenuItems.find(submenuItem => urls.includes(submenuItem?.url))
    return urls.includes(url) || submenuItem
  }, [location.pathname])

  const isMenuTarget = target?.current === menuItemRef?.current
  const isExpired = (requiredSub && (requiredSub.isExpired || !requiredSub.exists))

  const rootClassName = clsx(
    isMB ? classes.rootMbo : classes.rootOmo,
    isActive && (isMB ? classes.activeMbo : classes.activeOmo),
    isExpired && classes.expired,
    (isMenuTarget && isOpen) && (isMB ? classes.openMbo : classes.openOmo),
  )

  const openMenuPopover = () => {
    setTarget(menuItemRef)
    setMenuItems(submenuItems)
    setOpen(true)
  }

  useEffect(() => {
    if (!isOpen || !isMenuTarget) { return }
    setMenuItems(submenuItems)
  }, [submenuItems])

  const onItemClick = (e) => {
    e.preventDefault()

    if (!isMB && name === MenuItems.Marketplace && !notTrialSub) {
      openUpgradeModal()
      return
    }

    if (!isIncludedInPlan) {
      return openUpgradeModal(
        {
          limitReached: subscriptionLimits.DEFAULT_NOTINPLAN,
          subscriptionChange: true,
        },
      )
    }

    if (requiredSub) {
      const { exists, isExpired, type } = requiredSub
      if (!exists) {
        return openStartTrialModal({
          subscriptionType: type,
          onSuccess: () => {
            if (url) {
              return navigate(url, { state: { resetListPosition: true } })
            }
          },
        })
      }

      if (isExpired) {
        return openUpgradeModal({ subscriptionType: type })
      }
    }

    if (!submenuItems?.length) {
      setOpen(false)
      return navigate(url, { state: { resetListPosition: true } })
    }

    openMenuPopover()
  }

  const onItemMouseOver = useCallback((): void => {
    if (!isOpen) { return }
    openMenuPopover()
  }, [isOpen, submenuItems])

  // FLAG:useDocuAi — a menu item whose `name` has no entry in MenuItemIcons would
  // hand `undefined` to <SVGIcon>, which logs the confusing "SvgUrlOrComponent is
  // not defined" warning. Flag-on, we skip rendering the icon when none is mapped
  // (the wrapper stays, so layout is unchanged — baseline already rendered null here).
  // Flag-off renders exactly as before — unconditionally — preserving baseline behavior.
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const menuItemIcon = MenuItemIcons[name]
  const showMenuItemIcon = !useDocuAiEnabled || Boolean(menuItemIcon)

  const menuIcon = (showOnlyIcon || menuSettings.type === MenuTypes.comfortable) && (
    <div className={clsx(classes.iconWrapper, badgeLabel && classes.badgeLabelIcon)}>
      {(showOnlyIcon && badgeLabel) && <span className={classes.badge}>{badgeLabel}</span>}
      {showMenuItemIcon && <SVGIcon
        height={menuIconSize.height}
        width={menuIconSize.width}
        svg={menuItemIcon}
        className={clsx(classes.icon, iconClassName)} />}
    </div>
  )
  const arrowIcon = submenuItems?.length > 0 && (
    <SVGIcon height='10px' width='10px' svg={ArrowUp} className={classes.arrow} />
  )

  const Element = submenuItems.length === 0 ? NavLink : 'div'
  return (
    <>
      <Element
        {...!submenuItems.length ? { 'data-nav': 'menuV2Item' } : {}}
        {...!submenuItems.length ? { 'data-pathname': url } : {}}
        ref={menuItemRef}
        id={itemId}
        data-qs-id={itemId}
        to={url}
        state={{ resetListPosition: true }}
        className={rootClassName}
        data-manual-navigate={true}
        onClick={onItemClick}
        onMouseOver={onItemMouseOver}>
        {menuIcon}
        {!showOnlyIcon && (
          <div className={clsx(classes.titleWrapper, badgeLabel && classes.badgeLabelIcon)}>
            <span className={classes.title}>{title}</span>
            {badgeLabel && <span className={classes.badge}>{badgeLabel}</span>}
            {arrowIcon}
          </div>
        )}
      </Element>
    </>
  )
}

export default MenuBarItem
